import { Button, Modal } from '@ce-cfbdigital/cgl-cfb-fe-foundation';
import PropTypes from 'prop-types';

import AccountAndRegionSelection from '../AccountAndRegionSelection';
import CargillLogo from '../CargillLogo';
import CustomIcon from '../CustomIcons';
import GlobalNav from '../GlobalNav';
import LdsRing from '../LdsRing';
import NotificationsBadge from '../NotificationsBadge';
import { getAppConfig, handleLogout, updateSessionStorage } from '../utilities';
import { isUserExternal } from '../utilities/auth';
import { redirectTo } from '../utilities/common';
import AccountRegionButton from './components/AccountRegionButton';
import CartIcon from './components/CartIcon';
import HamburguerIcon from './components/Hamburger';
import useAccount from './customHooks/useAccount';
import useConfig from './customHooks/useConfig';
import useHeader from './customHooks/useHeader';
import useModalAndGlobalNav from './customHooks/useModalAndGlobalNav';
import useNavigationData from './customHooks/useNavigationData';

import './styles/index.css';

export const Logout = ({ handleLogoutInterface, logoutLabel }) => (
  <div className="header__logout">
    <button
      onClick={handleLogoutInterface}
      className="header__col-2--btn header__btn flex-group"
      type="button"
      tabIndex={0}
    >
      <CustomIcon type="logout" size="24" className="logout-icon" fill="#FFFFFF" />
      <span>{logoutLabel}</span>
    </button>
  </div>
);

const getLdsRing = (ldsClass = '') =>
  ldsClass ? (
    <div className={ldsClass}>
      <LdsRing />
    </div>
  ) : (
    <LdsRing />
  );

const Header = ({ hideNavigation, hideCart, hideNotificationBadge }) => {
  const location = window.location.pathname;
  const { path = '', host } = getAppConfig('server');
  if (!location.includes('/food/onboarding')) {
    const { lang: locale = '' } = getAppConfig('locale');
    updateSessionStorage.setItem('fibiPath', path);
    updateSessionStorage.setItem('fibiHost', host);
    updateSessionStorage.setItem('locale', locale);
  }
  const fibiPath = updateSessionStorage.getItem('fibiPath');
  const cartPageUrl = `${path}/orderbuilder`;
  const homePageUrl = `${fibiPath}/dashboard`;
  const { notificationConfig, headerConfig, redirectBaseUrl, page } = useConfig();
  const { isShowCart, userGroups, cartItemCount, isDeleteCart, handleDeleteModal, handleLogoutInterface } = useHeader({
    pathname: location,
    hideCart
  });
  const {
    accountAndRegion,
    updateAccountSelection,
    updateRegionSelection,
    selectedAccount,
    selectedRegion,
    isRegionalAccount
  } = hideNavigation ? { selectedAccount: {}, selectedRegion: {} } : useAccount(location);
  const {
    isAccountModalOpen,
    isRegionModalOpen,
    handleAccountModal,
    handleRegionCloseModal,
    isGlobalNavExpanded,
    handleGlobalNavigation
  } = useModalAndGlobalNav(isRegionalAccount, location);
  const { navigationData = [] } = !hideNavigation && useNavigationData(headerConfig, userGroups);
  const {
    labels: {
      logoutLabel,
      selectAccount,
      selectRegion,
      cancelButtonLabel,
      confirmButtonLabel,
      noOptions,
      yesLabel,
      clearCartHeading,
      cleatCartMessage,
      loaderNavLabel
    }
  } = headerConfig || {
    labels: {
      logoutLabel: '',
      selectAccount: '',
      selectRegion: '',
      cancelButtonLabel: '',
      confirmButtonLabel: '',
      noOptions: '',
      yesLabel: '',
      clearCartHeading: '',
      cleatCartMessage: '',
      loaderNavLabel: ''
    }
  };
  const { accounts, regions } = accountAndRegion || { accounts: [], regions: [] };
  const hasMultipleAccounts = accounts.length > 1;
  const hasMultipleRegions = regions.length > 1;

  if (redirectBaseUrl) {
    redirectTo(page);
  }

  return (
    <div className="header">
      <header className="header__wrapper flex-group">
        <div className="header__col--1 flex-group">
          {navigationData
            ? !!navigationData.length && (
                <HamburguerIcon handleOpenDrawer={handleGlobalNavigation} isGlobalNavExpanded={isGlobalNavExpanded} />
              )
            : getLdsRing()}
          <CargillLogo homePageUrl={homePageUrl} />
        </div>
        <div className="header__col--2 flex-group">
          {location && !location.includes('/food/onboarding') && (
            <>
              {selectedRegion
                ? hasMultipleRegions && (
                    <div className="header__region">
                      <AccountRegionButton
                        clickHandler={handleRegionCloseModal}
                        shortName={selectedRegion.shortname}
                        arrowStatus={isRegionModalOpen}
                        iconType="language"
                      />
                    </div>
                  )
                : getLdsRing('header__region')}

              {selectedAccount
                ? hasMultipleAccounts && (
                    <div className="header__account">
                      <AccountRegionButton
                        clickHandler={handleAccountModal}
                        shortName={selectedAccount.shortname}
                        arrowStatus={isAccountModalOpen}
                        iconType="account"
                      />
                    </div>
                  )
                : getLdsRing('header__account')}
              {!hideNotificationBadge && isUserExternal() && userGroups && notificationConfig && (
                <div className="header__notifications">
                  <NotificationsBadge config={notificationConfig} show={false} path={location} groups={userGroups} />
                </div>
              )}
              {!hideCart && isShowCart && <CartIcon cartPageUrl={cartPageUrl} cartItemCount={cartItemCount} />}
            </>
          )}
          <Logout handleLogoutInterface={handleLogoutInterface} logoutLabel={logoutLabel} />
        </div>
      </header>
      {!!isAccountModalOpen && (
        <div role="presentation" className="modal-overlay" onClick={handleAccountModal} onKeyUp={handleAccountModal}>
          <div
            role="presentation"
            className="modal-content"
            onClick={e => e.stopPropagation()}
            onKeyUp={e => e.stopPropagation()}
          >
            <AccountAndRegionSelection
              headerLabel={selectAccount}
              noOptions={noOptions}
              selectOptions={accountAndRegion.accounts}
              classNamePrefix="account-select"
              defaultValue={accountAndRegion.accounts.filter(option => option.selected)[0]}
              closeModal={handleAccountModal}
              callbackMethod={updateAccountSelection}
              confirmButtonLabel={confirmButtonLabel}
              cancelButtonLabel={cancelButtonLabel}
            />
          </div>
        </div>
      )}
      {!!isRegionModalOpen && (
        <div
          role="presentation"
          className="modal-overlay"
          onClick={handleRegionCloseModal}
          onKeyUp={handleRegionCloseModal}
        >
          <div
            role="presentation"
            className="modal-content"
            onClick={e => e.stopPropagation()}
            onKeyUp={e => e.stopPropagation()}
          >
            <AccountAndRegionSelection
              selectOptions={accountAndRegion.regions}
              noOptions={noOptions}
              headerLabel={selectRegion}
              classNamePrefix="account-select"
              defaultValue={accountAndRegion.regions.filter(option => option.selected)[0]}
              closeModal={handleRegionCloseModal}
              callbackMethod={updateRegionSelection}
              confirmButtonLabel={confirmButtonLabel}
              cancelButtonLabel={cancelButtonLabel}
            />
          </div>
        </div>
      )}
      {isDeleteCart && (
        <Modal handleCloseModal={handleDeleteModal}>
          <div className="clear-cart-popup">
            <h3 className="alpha b">{clearCartHeading}</h3>
            <p className="b">{cleatCartMessage}</p>
            <Button
              buttonLabel={cancelButtonLabel}
              classes="btn button button"
              type="submit"
              handleClick={() => handleDeleteModal()}
            />
            <Button
              buttonLabel={yesLabel}
              classes="btn button button"
              type="submit"
              handleClick={() => handleLogout()}
            />
          </div>
        </Modal>
      )}
      <div className={`global-nav ${(isGlobalNavExpanded && 'visible') || ''}`}>
        {navigationData && (
          <GlobalNav
            navigationData={navigationData}
            handleGlobalNavigation={handleGlobalNavigation}
            pathname={location}
            isGlobalNavExpanded={isGlobalNavExpanded}
            loaderNavLabel={loaderNavLabel}
          />
        )}
      </div>
    </div>
  );
};

Header.propTypes = {
  hideCart: PropTypes.bool,
  hideNotificationBadge: PropTypes.bool,
  hideNavigation: PropTypes.bool
};

Header.defaultProps = {
  hideCart: false,
  hideNotificationBadge: false,
  hideNavigation: false
};

Logout.propTypes = {
  handleLogoutInterface: PropTypes.func.isRequired,
  logoutLabel: PropTypes.string.isRequired
};
export { Header };
export default Header;
